import { Building2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateCustomer,
	useCustomers,
	useDeleteCustomer,
	useUpdateCustomer,
} from '@/api/queries/customers-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { CustomerFormContent } from '@/components/administration'
import {
	AdminCardPage,
	CardListItem,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type {
	CustomerInput,
	CustomerOutput,
	UpdateCustomerBody,
} from '@/types/customer'
import type { OrgBranch } from '@/types/organization'
import { getApiErrorMessage } from '@/utils/api-error'

export const CustomersPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [search, setSearch] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<CustomerOutput | undefined>(undefined)
	const [deleteTarget, setDeleteTarget] = useState<CustomerOutput | undefined>(
		undefined,
	)

	const { data: customersData, isLoading: customersLoading } = useCustomers({
		search: search.trim() || undefined,
		page: 1,
		limit: 100,
	})
	const { data: organization, isLoading: orgLoading } = useOrganization()

	const createMutation = useCreateCustomer()
	const updateMutation = useUpdateCustomer()
	const deleteMutation = useDeleteCustomer()

	const customers = customersData?.items ?? []
	const total = customersData?.pagination.total ?? 0
	const branches = organization ?? []
	// branches that were system-created (to be shown in the CREATE customer modal)
	const systemBranches = branches.filter((b: OrgBranch) => b.isSystemCreated)
	const isLoading = customersLoading || orgLoading

	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (customer: CustomerOutput) => {
		setEditing(customer)
		setFormOpen(true)
	}

	const handleSave = async (data: CustomerInput | UpdateCustomerBody) => {
		if (editing) {
			try {
				await updateMutation.mutateAsync({
					id: editing.id,
					body: data as UpdateCustomerBody,
				})
				toast.success(
					t('administration.customers.editSuccess', { name: editing.name }),
				)
				setFormOpen(false)
				setEditing(undefined)
			} catch (error) {
				toast.error(getApiErrorMessage(error))
			}
		} else {
			try {
				await createMutation.mutateAsync(data as CustomerInput)
				toast.success(t('administration.customers.createSuccess'))
				setFormOpen(false)
			} catch (error) {
				toast.error(getApiErrorMessage(error))
			}
		}
	}

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return
		try {
			await deleteMutation.mutateAsync(deleteTarget.id)
			toast.success(
				t('administration.customers.deleteSuccess', {
					name: deleteTarget.name,
				}),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
		setDeleteTarget(undefined)
	}

	const formTitle = editing
		? t('administration.customers.editCustomer')
		: t('administration.customers.createNew')

	return (
		<>
			<AdminCardPage
				icon={<Building2 className="size-5" />}
				title={t('administration.customers.title')}
				subtitle={t('administration.customers.subtitle')}
				onBack={() => navigate(adminBackRoute(ROUTES.ADMINISTRATION.CUSTOMERS))}
				footerText={`${total} ${
					total === 1
						? t('administration.customers.countSingular')
						: t('administration.customers.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.customers.createNew')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={formOpen}
						onOpenChange={setFormOpen}
						title={formTitle}
						dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-lg"
					>
						<CustomerFormContent
							mode={editing ? 'edit' : 'create'}
							initial={editing}
							branches={systemBranches}
							onSave={handleSave}
							onClose={() => setFormOpen(false)}
						/>
					</ResponsiveFormModal>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t('administration.customers.searchPlaceholder')}
				/>

				{isLoading ? (
					<div className="flex items-center justify-center py-16">
						<span className="text-sm text-muted-foreground">
							{t('administration.customers.loading')}
						</span>
					</div>
				) : customers.length === 0 ? (
					<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
						<Building2 className="size-8 text-kaefer-light-silver" />
						<p className="text-sm">{t('administration.customers.noResults')}</p>
					</div>
				) : (
					<div className="space-y-2">
						{customers.map((customer) => (
							<CardListItem
								key={customer.id}
								title={customer.name}
								details={[
									{
										label: t('administration.customers.contractLabel'),
										value: customer.cr.customName || customer.cr.name,
									},
								]}
								onEdit={() => handleOpenEdit(customer)}
								onDelete={() => setDeleteTarget(customer)}
							/>
						))}
					</div>
				)}
			</AdminCardPage>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => !open && setDeleteTarget(undefined)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.customers.deleteTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.customers.deleteDescription', {
								name: deleteTarget?.name ?? '',
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('administration.customers.deleteCancelButton')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteConfirm}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{t('administration.customers.deleteConfirmButton')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
